/**
 * Patient 360 View Model & Normalization Layer
 *
 * This is the strict data contract for the Patient 360 UI.
 * ALL components must consume Patient360ViewModel — never raw API DTOs.
 *
 * Data flow:
 *   Backend API → Patient360ResponseDto (raw DTO)
 *     ↓ normalizePatient360()
 *   Patient360ViewModel (clean contract, UI-safe)
 *
 * Rules enforced here:
 *  - Title-only strings (e.g. "Dr.") are never returned as HCP name
 *  - TotalAssistance falls back to TotalApproved when TotalCovered is 0
 *  - All string fields always yield a non-null/non-empty display value
 *  - All number fields always yield 0 rather than undefined/null
 */

import type {
  Patient360ResponseDto,
  PatientSummaryDto,
  PatientFinancialSummaryDto,
  PatientCaseSummaryDto,
  Patient360SchemeBreakdownDto,
  Patient360FinancialDto,
  Patient360CaseDto,
  Patient360CallDto,
  Patient360AdverseEventDto,
  Patient360WorkflowItemDto,
  Patient360NotificationItemDto,
  Patient360ClaimDto,
  Patient360DocumentDto,
  Patient360CycleTimelineItemDto,
  Patient360UnifiedTimelineCycleDto,
} from './patient360';
import { entityDisplay } from '@/modules/shared/utils/entityDisplay';

// ─── Sentinel values (used for "not available" display) ──────
export const NOT_AVAILABLE = 'Not available';
export const NOT_ASSIGNED = 'Not assigned';
export const DASH = '—';

// ─── Title regex: prefixes that are NOT standalone names ─────
const TITLE_ONLY_RE = /^(dr\.?|prof\.?|mr\.?|mrs\.?|ms\.?|sr\.?|jr\.?)$/i;

// ─── Display format helpers (private — used only by normalizer) ──────────────

/** Safe fallback — returns value if non-empty, else '—' */
function fmt(v: string | null | undefined): string {
  return (v && v.trim()) ? v.trim() : DASH;
}

/** Formats an ISO date string to "Jan 15, 1985" style (same locale as app) */
function fmtDate(v: string | null | undefined): string {
  if (!v) return DASH;
  try {
    const d = new Date(v);
    if (isNaN(d.getTime()) || d.getFullYear() < 1900) return DASH;
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return DASH;
  }
}

/** DOB with optional inline age: "Jan 15, 1985 (39 yrs)" */
function fmtDob(v: string | null | undefined, age: number | null | undefined): string {
  const datePart = fmtDate(v);
  if (datePart === DASH) return DASH;
  if (age != null && age > 0) return `${datePart} (${age} yrs)`;
  // derive age if not provided
  try {
    const years = Math.floor((Date.now() - new Date(v!).getTime()) / (365.25 * 24 * 3600 * 1000));
    if (years > 0 && years < 150) return `${datePart} (${years} yrs)`;
  } catch { /* ignore */ }
  return datePart;
}

/**
 * Looks up a value from a formDataFields record using normalised key matching.
 * Keys are compared case-insensitively with underscores/spaces/hyphens stripped.
 */
function pickFormField(fields: Record<string, unknown>, ...keys: string[]): string | null {
  const norm = (s: string) => s.toLowerCase().replace(/[_\s-]/g, '');
  for (const k of keys) {
    const entry = Object.entries(fields).find(([key]) => norm(key) === norm(k));
    if (entry && entry[1] != null && entry[1] !== '') return String(entry[1]);
  }
  return null;
}

// ─── Display Contract ──────────────────────────────────────────

/**
 * All fields pre-formatted and guaranteed non-null.
 * UI components MUST use these instead of formatting raw vm fields.
 *
 * "—" is the canonical empty-value sentinel for display.
 */
export interface Patient360DisplayModel {
  // Identity
  fullName: string;
  patientCode: string;
  hospitalName: string;
  hcpName: string;
  // Demographics (always a display-safe string, never null)
  gender: string;
  dateOfBirth: string;     // "Jan 15, 1985 (39 yrs)" or "—"
  age: string;             // "39 yrs" or "—"
  email: string;
  contactNumber: string;
  // Clinical
  diagnosis: string;
  schemeType: string;
  /** Patient registration approval status (APPROVED/PENDING/REJECTED) — distinct from case workflow state. */
  approvalStatus: string;
  consentGiven: string;    // "Yes" | "No" | "—"
  enrollmentNotes: string;
  // Enrollment
  enrolledOn: string;      // formatted date or "—"
}

// ─── Clean Contract ──────────────────────────────────────────

/**
 * Normalized, UI-safe view model for the Patient 360 page.
 * Every field is guaranteed to be present and safe to render directly.
 */
export interface Patient360ViewModel {
  // Identifiers
  patientId: string;
  programId: string;
  patientCode: string;

  // Display name
  fullName: string;

  // Status
  status: string;
  workflowState: string | null;
  currentWorkflowStepName: string | null;

  // Care assignments
  hospitalName: string;
  hcpName: string;

  // Demographics
  gender: string | null;
  dateOfBirth: string | null;
  age: number | null;
  contactNumber: string | null;
  email: string | null;
  diagnosis: string | null;

  // Financial KPIs
  /** Total assigned = utilizedAmount + remainingEligibility */
  totalAssistance: number;
  /** Amount already used/covered */
  utilizedAmount: number;
  /** Remaining available balance */
  remainingEligibility: number;
  /** True when no case for this patient has a configured cap — remainingEligibility is not a real balance, low-balance/top-up logic must not fire */
  isUnlimited: boolean;

  /** Scheme breakdown for OverviewTab and FinancialsTab */
  schemeBreakdown: Patient360SchemeBreakdownDto[];

  // Case KPIs
  activeCases: number;
  completedCases: number;
  totalCycles: number;
  completedCycles: number;
  hasActiveCase: boolean;
  hasActiveCycle: boolean;
  activeCycleId: string | null;

  // Timeline marker
  lastInteraction: string | null;

  // Pass-through data for tabs (still typed, validated presence)
  cases: Patient360CaseDto[];
  calls: Patient360CallDto[];
  adverseEvents: Patient360AdverseEventDto[];
  workflow: Patient360WorkflowItemDto[];
  notifications: Patient360NotificationItemDto[];
  claims: Patient360ClaimDto[];
  documents: Patient360DocumentDto[];
  cycleTimeline: Patient360CycleTimelineItemDto[];
  unifiedTimeline: Patient360UnifiedTimelineCycleDto[];

  // Full financial object (for FinancialsTab)
  financial: Patient360FinancialDto;

  /** ISO 4217 currency code derived from ledger data. Defaults to 'USD'. */
  currencyCode: string;

  /**
   * Pre-formatted, display-ready strings for all patient identity & clinical fields.
   * UI components MUST use these — never format raw vm fields inline.
   * Every field is guaranteed non-null; '—' is the empty sentinel.
   */
  display: Patient360DisplayModel;

  // Raw patient/summary for components that still need the typed sub-objects
  _raw: {
    patient: PatientSummaryDto;
    financialSummary: PatientFinancialSummaryDto;
    caseSummary: PatientCaseSummaryDto;
  };
}

// ─── Normalizer ───────────────────────────────────────────────

/**
 * Normalizes a raw Patient360ResponseDto into a clean Patient360ViewModel.
 * Backward-compatible: handles both the old API shape (`summary`) and the new
 * shape (`financialSummary` + `caseSummary`). Never throws.
 */
export function normalizePatient360(raw: Patient360ResponseDto): Patient360ViewModel {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rawAny = raw as any;

  const p = raw.patient ?? rawAny.patient ?? {};
  const f = raw.financial ?? rawAny.financial ?? { ledger: [], utilization: {} };

  // ── Backward-compatible summary resolution ───────────────────
  // New API:  raw.financialSummary + raw.caseSummary
  // Old API:  raw.summary  (all fields in one object)
  const legacySummary = rawAny.summary ?? {};
  const fs = raw.financialSummary ?? {
    totalAssistance: legacySummary.totalAssistance ?? 0,
    remainingEligibility: legacySummary.remainingEligibility ?? 0,
    isUnlimited: legacySummary.isUnlimited ?? false,
    lastInteraction: legacySummary.lastInteraction ?? null,
    schemeBreakdown: legacySummary.schemeBreakdown ?? [],
  };
  const cs = raw.caseSummary ?? {
    activeCases: legacySummary.activeCases ?? 0,
    completedCases: legacySummary.completedCases ?? 0,
    totalCycles: legacySummary.totalCycles ?? 0,
    completedCycles: legacySummary.completedCycles ?? 0,
    activeCaseId: null,
    activeCaseCode: 'No active enrollment',
  };
  const hasActiveCase = typeof raw.hasActiveCase === 'boolean'
    ? raw.hasActiveCase
    : (cs.activeCases ?? 0) > 0;
  const activeCycleId = raw.activeCycleId
    ?? raw.cycleTimeline?.find((item) => item.caseId === cs.activeCaseId)?.cycleId
    ?? null;
  const hasActiveCycle = typeof raw.hasActiveCycle === 'boolean'
    ? raw.hasActiveCycle
    : (raw.cases ?? []).some((item) => item.isActive && item.currentCycleNumber > 0);

  // ── Backward-compatible patient field resolution ─────────────
  // New API: p.fullName    Old API: p.patientName
  const fullName = (p.fullName ?? rawAny.patient?.patientName ?? '').trim() || NOT_AVAILABLE;
  const resolvedPatientCode = p.patientCode ?? rawAny.patient?.code ?? rawAny.patient?.displayCode ?? '';
  if (!resolvedPatientCode) {
    console.warn('[patient360Model] Missing patient code in API payload.', {
      patientId: p.patientId ?? rawAny.patient?.patientId ?? null,
      availableKeys: rawAny.patient ? Object.keys(rawAny.patient) : [],
    });
  }
  const patientIdentity = entityDisplay.patient({
    patientCode: resolvedPatientCode,
    fullName,
    patientName: rawAny.patient?.patientName,
  });
  // New API: p.hcpName already clean   Old API: may be title-only
  const rawHcpName = (p.hcpName ?? rawAny.patient?.hcp?.name ?? '').trim();
  const hcpName = rawHcpName && !TITLE_ONLY_RE.test(rawHcpName) ? rawHcpName : NOT_ASSIGNED;

  // ── Utilized Amount (from backend or fallback) ─────────────────
  const utilizedAmount = Math.max(
    (fs as any).utilizedAmount > 0
      ? (fs as any).utilizedAmount
      : (f?.utilization?.totalCovered ?? f?.utilization?.totalApproved ?? 0),
    0,
  );

  // ── Remaining Eligibility ────────────────────────────────────
  const remainingEligibility = Math.max(
    fs.remainingEligibility > 0
      ? fs.remainingEligibility
      : (f?.utilization?.remainingBenefit ?? 0),
    0,
  );

  // ── Total Assistance = utilized + remaining ──────────────────
  const totalAssistance =
    fs.totalAssistance > 0
      ? fs.totalAssistance
      : (utilizedAmount + remainingEligibility);

  return {
    patientId: p.patientId ?? '',
    programId: p.programId ?? '',
    patientCode: patientIdentity.label,

    fullName: patientIdentity.subLabel,

    status: p.status || 'Unknown',
    workflowState: p.workflowState ?? null,
    currentWorkflowStepName: p.currentWorkflowStepName ?? null,

    hospitalName: p.hospitalName || NOT_ASSIGNED,
    hcpName,

    gender: p.gender ?? null,
    dateOfBirth: p.dateOfBirth ?? null,
    age: p.age ?? null,
    contactNumber: p.contactNumber ?? null,
    email: p.email ?? null,
    diagnosis: p.diagnosis ?? null,

    totalAssistance,
    utilizedAmount,
    remainingEligibility,
    isUnlimited: fs.isUnlimited ?? false,
    schemeBreakdown: fs.schemeBreakdown ?? [],

    activeCases: cs.activeCases,
    completedCases: cs.completedCases,
    totalCycles: cs.totalCycles,
    completedCycles: cs.completedCycles,
    hasActiveCase,
    hasActiveCycle,
    activeCycleId,

    lastInteraction: fs.lastInteraction ?? null,

    cases: raw.cases ?? [],
    calls: raw.calls ?? [],
    adverseEvents: raw.adverseEvents ?? [],
    workflow: raw.workflow ?? [],
    notifications: raw.notifications ?? [],
    claims: raw.claims ?? [],
    documents: raw.documents ?? [],
    cycleTimeline: raw.cycleTimeline ?? [],
    unifiedTimeline: raw.unifiedTimeline ?? [],

    financial: f,

    currencyCode:
      f?.ledger?.find(l => l.baseCurrencyCode)?.baseCurrencyCode ??
      f?.ledger?.find(l => l.currencyCode)?.currencyCode ??
      'USD',

    // ── Display model: pre-formatted, UI-ready, always non-null ─────────────
    // Resolves static columns first, then falls back to dynamic formDataFields.
    // UI must use vm.display.* — never format raw fields inline.
    display: (() => {
      const ff = p.formDataFields ?? {};
      const genderVal       = p.gender        ?? pickFormField(ff, 'gender') ?? null;
      const dobVal          = p.dateOfBirth   ?? pickFormField(ff, 'dateOfBirth', 'dob', 'date_of_birth') ?? null;
      const emailVal        = p.email         ?? pickFormField(ff, 'email', 'emailAddress') ?? null;
      const contactVal      = p.contactNumber ?? pickFormField(ff, 'contactNumber', 'phone', 'mobile') ?? null;
      const diagnosisVal    = p.diagnosis     ?? pickFormField(ff, 'diagnosis', 'primaryDiagnosis', 'medicalCondition') ?? null;
      const schemeVal       = (p as any).schemeType ?? pickFormField(ff, 'schemeType', 'scheme_type', 'assistanceScheme', 'treatmentType') ?? null;
      const approvalStatusVal = p.approvalStatus ?? null;
      const consentVal      = p.consentGiven != null ? (p.consentGiven ? 'Yes' : 'No') : (pickFormField(ff, 'consent', 'consentGiven', 'patientConsent') ?? DASH);
      const notesVal        = p.enrollmentNotes ?? pickFormField(ff, 'enrollmentNotes', 'enrollment_notes', 'notes', 'remarks') ?? null;
      return {
        fullName:        patientIdentity.subLabel,
        patientCode:     patientIdentity.label,
        hospitalName:    p.hospitalName || NOT_ASSIGNED,
        hcpName,
        gender:          fmt(genderVal),
        dateOfBirth:     fmtDob(dobVal, p.age),
        age:             p.age != null && p.age > 0 ? `${p.age} yrs` : DASH,
        email:           fmt(emailVal),
        contactNumber:   fmt(contactVal),
        diagnosis:       fmt(diagnosisVal),
        schemeType:      fmt(schemeVal),
        approvalStatus:  fmt(approvalStatusVal),
        consentGiven:    consentVal,
        enrollmentNotes: fmt(notesVal),
        enrolledOn:      fmtDate(p.createdAt),
      } satisfies Patient360DisplayModel;
    })(),

    _raw: { patient: p, financialSummary: fs, caseSummary: cs },
  };
}

// ─── Validation ───────────────────────────────────────────────

export interface Patient360ValidationResult {
  isValid: boolean;
  warnings: string[];
}

/**
 * Validates a normalized Patient360ViewModel for completeness.
 * Returns warnings (non-blocking) to aid debugging across programs.
 * Never throws — purely informational.
 */
export function validatePatient360(vm: Patient360ViewModel): Patient360ValidationResult {
  const warnings: string[] = [];

  if (!vm.fullName || vm.fullName === NOT_AVAILABLE) {
    warnings.push('Patient name is missing or unresolved');
  }
  if (vm.hospitalName === NOT_ASSIGNED) {
    warnings.push('Hospital assignment not found in FormData');
  }
  if (vm.hcpName === NOT_ASSIGNED) {
    warnings.push('HCP name not found — FormData may only contain a title prefix');
  }
  if (!vm.dateOfBirth) {
    warnings.push('Date of birth missing from patient record');
  }
  if (vm.patientCode === '—') {
    warnings.push('Patient code is missing or resolved to an internal identifier');
  }

  return {
    isValid: warnings.length === 0,
    warnings,
  };
}

// ─── Aliases ──────────────────────────────────────────────────

/**
 * Canonical entry point for normalizing API responses.
 * Alias for `normalizePatient360()`.
 * Use:
 *   const vm = mapPatientToViewModel(apiResponse);
 */
export const mapPatientToViewModel = normalizePatient360;

// ─── Empty / Loading sentinel ─────────────────────────────────

/**
 * A fully-shaped empty Patient360ViewModel used as a safe placeholder
 * when real data is not yet loaded.
 *
 * PURPOSE: hooks (usePatientAlerts, usePatientLifecycleStage, usePredictiveRisks)
 * are called unconditionally (Rules of Hooks), so they must always receive a
 * properly-shaped object — never an empty `{}` cast. Using this sentinel
 * eliminates the entire class of "Cannot read properties of undefined" crashes.
 *
 * Usage in page:
 *   const safeVm = vm ?? EMPTY_PATIENT_360_VM;
 */
export const EMPTY_PATIENT_360_VM: Patient360ViewModel = {
  patientId: '',
  programId: '',
  patientCode: '—',
  fullName: NOT_AVAILABLE,
  status: 'Unknown',
  workflowState: null,
  currentWorkflowStepName: null,
  hospitalName: NOT_ASSIGNED,
  hcpName: NOT_ASSIGNED,
  gender: null,
  dateOfBirth: null,
  age: null,
  contactNumber: null,
  email: null,
  diagnosis: null,
  totalAssistance: 0,
  utilizedAmount: 0,
  remainingEligibility: 0,
  isUnlimited: false,
  schemeBreakdown: [],
  activeCases: 0,
  completedCases: 0,
  totalCycles: 0,
  completedCycles: 0,
  hasActiveCase: false,
  hasActiveCycle: false,
  activeCycleId: null,
  lastInteraction: null,
  cases: [],
  calls: [],
  adverseEvents: [],
  workflow: [],
  notifications: [],
  claims: [],
  documents: [],
  cycleTimeline: [],
  unifiedTimeline: [],
  financial: { ledger: [], utilization: {} } as unknown as import('./patient360').Patient360FinancialDto,
  currencyCode: 'USD',
  display: {
    fullName:        NOT_AVAILABLE,
    patientCode:     DASH,
    hospitalName:    NOT_ASSIGNED,
    hcpName:         NOT_ASSIGNED,
    gender:          DASH,
    dateOfBirth:     DASH,
    age:             DASH,
    email:           DASH,
    contactNumber:   DASH,
    diagnosis:       DASH,
    schemeType:      DASH,
    approvalStatus:  DASH,
    consentGiven:    DASH,
    enrollmentNotes: DASH,
    enrolledOn:      DASH,
  },
  _raw: {
    patient: {} as import('./patient360').PatientSummaryDto,
    financialSummary: {} as import('./patient360').PatientFinancialSummaryDto,
    caseSummary: {} as import('./patient360').PatientCaseSummaryDto,
  },
};
